import { Checkbox, FormControl, MenuItem, Select as MuiSelect } from '@mui/material';
import { useTranslate } from '@si/utils';
import { useId, useState } from 'react';

import { FormHelperText } from '../FormHelperText/FormHelperText';
import { FormLabel } from '../FormLabel/FormLabel';
import { useColors } from '../ThemeProvider';

interface Props<T extends string | number> {
  id?: string;
  label: string;
  options: { label: string; value: T }[];
  values: T[];
  onChange: (value: T[]) => void;
  renderValue?: (values: T[], options: { label: string; value: T }[]) => string;
  required?: boolean;
  helperText?: string;
  overrideVisited?: boolean;
  inputWidth?: '10rem' | '15rem' | '20rem' | '25rem' | '100%';
}

export function MultipleSelect<T extends string | number>({
  id,
  label,
  onChange,
  options,
  renderValue,
  values,
  required = false,
  helperText = '',
  overrideVisited = false,
  inputWidth = '100%',
}: Props<T>) {
  const { t, translate } = useTranslate();
  const { errorDark, textPrimary } = useColors();
  const generatedId = useId();

  const [visited, setVisited] = useState(false);

  const selectId = id ?? generatedId;
  const labelId = `${selectId}-label`;
  const helperTextId = `${selectId}-helper-text`;
  const error = required && values.length === 0;
  const showError = error && (visited || overrideVisited);

  const maxWidth = inputWidth === '10rem' || inputWidth === '15rem' ? '20rem' : inputWidth;
  const fieldColor = showError ? errorDark : textPrimary;

  return (
    <FormControl fullWidth sx={{ maxWidth }}>
      <FormLabel
        componentId={selectId}
        id={labelId}
        label={label}
        required={required}
        valid={!error}
      />

      <MuiSelect
        id={selectId}
        name={selectId}
        labelId={labelId}
        value={values}
        multiple
        onChange={({ target: { value } }) =>
          onChange((typeof value === 'string' ? value.split(',') : value) as T[])
        }
        onClose={() => setVisited(true)}
        error={showError}
        renderValue={(selected) =>
          renderValue
            ? renderValue(selected as T[], options)
            : selected.filter((val) => options.find((opt) => opt.value === val)).length > 1
              ? `${selected.length} ${translate(t.selected)}`
              : options.find((opt) => opt.value === selected[0])?.label.split(' ')[0] || ''
        }
        sx={{
          width: inputWidth,
          maxWidth: '100%',
          color: fieldColor,
          '& .MuiInputBase-input': {
            padding: '8px 12px',
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={values.includes(option.value)} />

            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>

      <FormHelperText
        id={helperTextId}
        showError={showError}
        text={showError ? translate(t.fieldRequired, { field: label }) : helperText}
      />
    </FormControl>
  );
}
